import { supabase } from 'src/lib/supabaseClient';
import { supabaseServiceRole } from 'src/lib/supabaseServiceClient';
import { useQuery } from '@tanstack/react-query';
import { calculateTutorStatus } from 'src/utils/tutorStatusUtils';

// Types for tutor data
export interface TutorEducation {
  gpa?: number;
  field: string;
  degree: string;
  country: string;
  institution: string;
  year_of_completion: number;
  is_custom_degree: boolean;
  gpa_total?: number;
  level: string;
}

export interface TutorExperience {
  role: "online" | "private" | "assessment" | "classroom" | "curriculum" | string;
  years: number;
  end_year?: number | null;
  location: string;
  start_year?: number | null;
  organization: string;
}

export interface TutorAvailabilitySlot {
  day_of_week: number; // 0 = Sunday, 6 = Saturday
  start_time: string;
  end_time: string;
  status: "available" | "auto_accept" | "manual_approval";
}

export interface TutorProfile {
  id: string;
  first_name?: string;
  last_name?: string;
  email?: string;
  profile_picture_url?: string;
  timezone?: string;
}

export interface Tutor {
  id: string;
  first_name: string;
  last_name: string;
  email: string;
  profile_picture_url?: string;
  timezone?: string;
  hourly_rate?: number;
  subjects_taught?: string[];
  bio?: string;
  verification_status?: string;
  cv_file_path?: string;
  rating?: number;
  date_of_birth?: string;
  created_at: string;
  updated_at: string;
  education?: TutorEducation[];
  experience?: TutorExperience[];
  achievements?: string[];
  expertise?: string[];
  total_years_exp?: number;
  is_live?: boolean;
  availability_slots?: TutorAvailabilitySlot[];
}

export interface TutorSearchFilters {
  searchTerm?: string;
  specialty?: string;
  availability?: string;
  priceRange?: string;
  minRating?: number;
}

export interface TutorData {
  tutors: Tutor[];
  profiles: Map<string, TutorProfile>;
  availability: Map<string, TutorAvailabilitySlot[]>;
}

// Query keys for react-query
export const TUTOR_QUERY_KEYS = {
  all: ['tutors'] as const,
  data: () => [...TUTOR_QUERY_KEYS.all, 'data', 'service-role-v2'] as const,
} as const;

class TutorsService {
  /**
   * Fetch all tutor data once - tutors, profiles, and availability
   * This is the single source of truth for all tutor data
   */
  async fetchAllTutorData(): Promise<TutorData> {
    try {
      // Fetch tutors data using service role - get all tutors first, then filter by active status
      const { data: allTutorsData, error: tutorsError } = await supabaseServiceRole
        .from("tutors")
        .select(`
          id,
          hourly_rate,
          subjects_taught,
          bio,
          verification_status,
          cv_file_path,
          rating,
          date_of_birth,
          created_at,
          updated_at,
          education,
          experience,
          achievements,
          expertise,
          total_years_exp,
          is_live
        `)
        .order("created_at", { ascending: false });

      if (tutorsError) throw tutorsError;

      if (!allTutorsData || allTutorsData.length === 0) {
        return {
          tutors: [],
          profiles: new Map(),
          availability: new Map()
        };
      }

      // Filter tutors to only include active ones using tutorStatusUtils logic
      // According to tutorStatusUtils, a tutor is 'active' when verification_status = 'approved' AND is_live = true
      const tutorsData = allTutorsData.filter(tutor => {
        const statusResult = calculateTutorStatus(tutor.verification_status, tutor.is_live);
        return statusResult.status === 'active';
      });

      if (tutorsData.length === 0) {
        return {
          tutors: [],
          profiles: new Map(),
          availability: new Map()
        };
      }

      // Get all tutor IDs
      const tutorIds = tutorsData.map(tutor => tutor.id);

      // Fetch profiles and availability data in parallel using service role
      const [profilesResult, availabilityResult] = await Promise.all([
        supabaseServiceRole
          .from("profiles")
          .select(`
            id,
            first_name,
            last_name,
            email,
            profile_picture_url,
            timezone
          `)
          .in('id', tutorIds),
        
        supabaseServiceRole
          .from("tutor_availability")
          .select(`
            tutor_id,
            day_of_week,
            start_time,
            end_time,
            status
          `)
          .in('tutor_id', tutorIds)
      ]);

      const { data: profilesData, error: profilesError } = profilesResult;
      const { data: availabilityData, error: availabilityError } = availabilityResult;

      if (profilesError) {
        console.error('Error fetching profiles:', profilesError);
      }

      if (availabilityError) {
        console.error('Error fetching availability:', availabilityError);
      }

      // Create maps for efficient lookup
      const profilesMap = new Map<string, TutorProfile>();
      if (profilesData) {
        profilesData.forEach(profile => {
          profilesMap.set(profile.id, profile);
        });
      }

      const availabilityMap = new Map<string, TutorAvailabilitySlot[]>();
      if (availabilityData) {
        availabilityData.forEach(slot => {
          if (!availabilityMap.has(slot.tutor_id)) {
            availabilityMap.set(slot.tutor_id, []);
          }
          availabilityMap.get(slot.tutor_id)!.push({
            day_of_week: slot.day_of_week,
            start_time: slot.start_time,
            end_time: slot.end_time,
            status: slot.status as "available" | "auto_accept" | "manual_approval"
          });
        });
      }

      // Transform tutors data
      const transformedTutors = tutorsData.map(tutor => {
        const profile = profilesMap.get(tutor.id);
        const subjects = tutor.subjects_taught || [];
        const availabilitySlots = availabilityMap.get(tutor.id) || [];

        return {
          ...tutor,
          first_name: profile?.first_name || '',
          last_name: profile?.last_name || '',
          email: profile?.email || '',
          profile_picture_url: profile?.profile_picture_url,
          timezone: profile?.timezone,
          subjects_taught: subjects,
          availability_slots: availabilitySlots
        };
      });

      return {
        tutors: transformedTutors,
        profiles: profilesMap,
        availability: availabilityMap
      };
    } catch (error) {
      console.error("Error fetching tutor data:", error);
      throw error;
    }
  }

  /**
   * Filter tutors by search criteria
   */
  filterTutors(tutors: Tutor[], filters: TutorSearchFilters): Tutor[] {
    let filteredData = [...tutors];

    // Search term filter (name, bio, subjects)
    if (filters.searchTerm) {
      const searchLower = filters.searchTerm.toLowerCase();
      filteredData = filteredData.filter(tutor =>
        tutor.first_name.toLowerCase().includes(searchLower) ||
        tutor.last_name.toLowerCase().includes(searchLower) ||
        (tutor.bio && tutor.bio.toLowerCase().includes(searchLower)) ||
        (tutor.subjects_taught && tutor.subjects_taught.some(subject =>
          subject.toLowerCase().includes(searchLower)
        )) ||
        (tutor.expertise && tutor.expertise.some(exp =>
          exp.toLowerCase().includes(searchLower)
        ))
      );
    }

    // Specialty filter
    if (filters.specialty && filters.specialty !== "all") {
      filteredData = filteredData.filter(tutor =>
        tutor.subjects_taught && tutor.subjects_taught.includes(filters.specialty!) ||
        tutor.expertise && tutor.expertise.includes(filters.specialty!)
      );
    }

    // Rating filter
    if (filters.minRating) {
      filteredData = filteredData.filter(tutor =>
        tutor.rating && tutor.rating >= filters.minRating!
      );
    }

    // Availability filter
    if (filters.availability && filters.availability !== "any") {
      const dayMap: { [key: string]: number } = {
        'Mon': 1, 'Tue': 2, 'Wed': 3, 'Thu': 4, 'Fri': 5, 'Sat': 6, 'Sun': 0
      };
      const dayOfWeek = dayMap[filters.availability];
      
      if (dayOfWeek !== undefined) {
        filteredData = filteredData.filter(tutor =>
          tutor.availability_slots && tutor.availability_slots.some(slot =>
            slot.day_of_week === dayOfWeek
          )
        );
      }
    }

    return filteredData;
  }

  /**
   * Find a tutor by ID from the cached data
   */
  findTutorById(tutors: Tutor[], id: string): Tutor | null {
    return tutors.find(tutor => tutor.id === id) || null;
  }

  /**
   * Get available time slots for booking a session with a tutor
   * This method follows the correct algorithm:
   * 1. Subtract/Block Unavailable Periods to get the available window
   * 2. Convert Availability to Tutor's Timezone
   * 3. Generate All Possible Slots
   */
  async getAvailableTimeSlotsNew(
    tutorId: string,
    selectedDate: Date,
    targetTimezone: string,
    sessionDurationMinutes: number = 60
  ): Promise<string[]> {
    try {
      // Import the booking availability service dynamically to avoid circular dependencies
      const { bookingAvailabilityService } = await import('./bookingAvailabilityService');
      
      const slots = await bookingAvailabilityService.getAvailableBookingSlots(
        tutorId,
        selectedDate,
        targetTimezone,
        sessionDurationMinutes
      );
      
      return bookingAvailabilityService.formatBookingSlotsForDisplay(slots);
    } catch (error) {
      console.error('Error getting available booking slots:', error);
      return [];
    }
  }
}

export const tutorsService = new TutorsService();

// React Query hooks for consuming tutor data
export const useTutorData = () => {
  return useQuery({
    queryKey: TUTOR_QUERY_KEYS.data(),
    queryFn: () => tutorsService.fetchAllTutorData(),
    staleTime: 1000 * 60 * 60, // 1 hour
    gcTime: 1000 * 60 * 60 * 2, // 2 hours (formerly cacheTime)
    refetchOnWindowFocus: false,
    refetchOnMount: false,
  });
};

// Hook to get all active tutors (approved and live)
export const useActiveTutors = () => {
  const { data, isLoading, error } = useTutorData();
  return {
    tutors: data?.tutors || [],
    isLoading,
    error
  };
};

// Hook to search tutors with filters
export const useSearchTutors = (filters: TutorSearchFilters) => {
  const { data, isLoading, error } = useTutorData();
  
  const filteredTutors = data?.tutors 
    ? tutorsService.filterTutors(data.tutors, filters)
    : [];

  return {
    tutors: filteredTutors,
    isLoading,
    error
  };
};

// Hook to get a single tutor by ID
export const useTutorById = (id: string) => {
  const { data, isLoading, error } = useTutorData();
  
  const tutor = data?.tutors 
    ? tutorsService.findTutorById(data.tutors, id)
    : null;

  return {
    tutor,
    isLoading,
    error
  };
};